import { createContext, useContext, useState, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { House, CaretRight } from "@phosphor-icons/react";
import { useAuth, homePathFor } from "@/lib/auth";

const BreadcrumbCtx = createContext({ label: null, setLabel: () => {} });

export function BreadcrumbProvider({ children }) {
  const [label, setLabel] = useState(null);
  return <BreadcrumbCtx.Provider value={{ label, setLabel }}>{children}</BreadcrumbCtx.Provider>;
}

// Detail pages call this to set the trailing crumb (e.g. a ticket/case number).
export function useBreadcrumb(label) {
  const { setLabel } = useContext(BreadcrumbCtx);
  useEffect(() => { setLabel(label || null); return () => setLabel(null); }, [label, setLabel]);
}

const LABELS = {
  incidents: "Incidents", problems: "Problems", changes: "Changes", assets: "Assets (CMDB)",
  kb: "Knowledge Base", reports: "Reports", "report-builder": "Report Builder", "client-report": "Client Report",
  sla: "SLA Policies", routing: "Routing Rules", users: "Users & Skills", security: "Security & Access",
  sessions: "Session Activity", admin: "Admin", groups: "Assignment Groups", canned: "Canned Responses",
  "email-ingest": "Email Ingest", events: "Alert Webhook", scim: "SCIM Provisioning", teams: "Teams Integration",
  notifications: "Email Notifications", portal: "End-User Portal", clients: "Clients",
  timesheet: "Timesheet", dashboard: "Dashboard", approvals: "Approvals",
  soc: "SOC", cases: "Cases", iocs: "IOC Search", trends: "Trends", settings: "Settings",
  tasks: "Tasks", projects: "Projects", all: "All Tasks", request: "Request Form",
};
const REPORT_VIEWS = { reports: "Reports", builder: "Report Builder", timesheet: "Timesheet-Reports" };
const ADMIN_TABS = {
  "alert-webhook": "Alert Webhook", "assignment-groups": "Assignment Groups", "canned-responses": "Canned Responses",
  "email-ingest": "Email Ingest", "email-notifications": "Email Notifications", "end-user-portal": "End-User Portal",
  scim: "SCIM Provisioning", sla: "SLA Policies", teams: "Teams Integration",
};

const isDynamic = (seg) => /^[0-9a-f]{8}-[0-9a-f-]{8,}$/i.test(seg) || /^[0-9a-f]{16,}$/i.test(seg) || /^\d+$/.test(seg);

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { label: dynLabel } = useContext(BreadcrumbCtx);
  const { user } = useAuth();

  const segs = pathname.split("/").filter(Boolean);
  const crumbs = [];
  let acc = "";
  segs.forEach((seg) => {
    acc += "/" + seg;
    const text = isDynamic(seg) ? (dynLabel || "Details") : (LABELS[seg] || seg);
    crumbs.push({ to: acc, text });
  });

  if (pathname === "/reports") {
    const v = params.get("view");
    if (REPORT_VIEWS[v] && v !== "reports") crumbs.push({ to: `/reports?view=${v}`, text: REPORT_VIEWS[v] });
  }
  if (pathname === "/admin") {
    const t = params.get("tab");
    if (ADMIN_TABS[t]) crumbs.push({ to: `/admin?tab=${t}`, text: ADMIN_TABS[t] });
  }

  return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-sidebar-muted dark:text-muted-foreground min-w-0">
      <Link to={homePathFor(user)} data-testid="breadcrumb-home" title="Home" className="hover:text-white dark:hover:text-foreground transition-colors shrink-0">
        <House size={13} />
      </Link>
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={`${c.to}-${i}`} className="flex items-center gap-1.5 min-w-0">
            <CaretRight size={10} className="opacity-50 shrink-0" />
            {last
              ? <span className="text-white dark:text-foreground truncate" data-testid="breadcrumb-current">{c.text}</span>
              : <Link to={c.to} data-testid={`breadcrumb-link-${c.text.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="hover:text-white dark:hover:text-foreground transition-colors truncate">{c.text}</Link>}
          </span>
        );
      })}
    </nav>
  );
}
